// Extract vivid dominant colors from an image for the premium card light-stripe
// layer. Downscales onto a tiny canvas, buckets pixels into coarse hue groups
// (skipping near-black/near-white/low-saturation background noise), and returns
// the top N most common vivid hues as hex strings.

const cache = new Map<string, Promise<string[]>>();

const rgbToHsl = (r: number, g: number, b: number) => {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };

  const s = d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (max === rN) h = ((gN - bN) / d) % 6;
  else if (max === gN) h = (bN - rN) / d + 2;
  else h = (rN - gN) / d + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;

  return { h, s, l };
};

const hslToHex = (h: number, s: number, l: number) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const HUE_BUCKET_SIZE = 24; // 15 buckets around the wheel

const extractFromImage = (img: HTMLImageElement, count: number): string[] => {
  const size = 32;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return [];

  ctx.drawImage(img, 0, 0, size, size);
  const { data } = ctx.getImageData(0, 0, size, size);

  // hue bucket -> { weight, sumS, sumL, count }
  const buckets = new Map<number, { weight: number; sumS: number; sumL: number; n: number }>();

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const a = data[i + 3]!;
    if (a < 200) continue;

    const { h, s, l } = rgbToHsl(r, g, b);
    if (s < 0.25 || l < 0.12 || l > 0.9) continue; // skip greys/near-black/near-white

    const bucket = Math.round(h / HUE_BUCKET_SIZE) * HUE_BUCKET_SIZE;
    const weight = s * (1 - Math.abs(l - 0.55)); // favor saturated, mid-lightness pixels
    const entry = buckets.get(bucket) ?? { weight: 0, sumS: 0, sumL: 0, n: 0 };
    entry.weight += weight;
    entry.sumS += s;
    entry.sumL += l;
    entry.n += 1;
    buckets.set(bucket, entry);
  }

  const sorted = Array.from(buckets.entries()).sort((a, b) => b[1].weight - a[1].weight);

  const picked: string[] = [];
  for (const [hue, entry] of sorted) {
    if (picked.length >= count) break;
    // avoid picking two buckets that are basically the same hue
    const tooClose = picked.some((hex) => {
      const n = parseInt(hex.slice(1), 16);
      const { h: pickedHue } = rgbToHsl((n >> 16) & 255, (n >> 8) & 255, n & 255);
      const diff = Math.min(Math.abs(pickedHue - hue), 360 - Math.abs(pickedHue - hue));
      return diff < HUE_BUCKET_SIZE * 1.5;
    });
    if (tooClose) continue;

    picked.push(hslToHex(hue, entry.sumS / entry.n, Math.min(0.62, entry.sumL / entry.n + 0.1)));
  }

  return picked;
};

/** Extract up to `count` vivid dominant colors from an image URL (memoized per URL+count). */
export const extractDominantColors = (url: string, count = 3): Promise<string[]> => {
  const cacheKey = `${url}::${count}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const promise = new Promise<string[]>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(extractFromImage(img, count));
    img.onerror = () => resolve([]);
    img.src = url;
  });

  cache.set(cacheKey, promise);
  return promise;
};
